import { Comment as TComment } from "@/apiTypes/comment";
import { Organizer } from "@/apiTypes/event";
import { FC, useState } from "react";
import { Avatar } from "../../ui/avatar";
import { getProfileName } from "@/utils/getProfileName";
import { formatRelativeTime } from "@/utils/displayFormatUtils";
import { Ellipsis, Trash2 } from "lucide-react";
import { Menu, MenuTrigger, MenuPopup, MenuItem } from "@/components/ui/menu";
import {
  AlertDialog,
  AlertDialogPopup,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogClose,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { deleteComment } from "@/server/comments";
import { toastManager } from "@/components/ui/toast";
import { Pill } from "@/components/ui/pill";

interface CommentProps {
  comment: TComment;
  clipped?: boolean;
  showRegisteredBadge?: boolean;
  organizer?: Organizer;
  canDelete?: boolean;
  onDelete?: (commentId: string) => void;
}

/**
 * Renders a single comment with author info, badges, and an optional
 * overflow menu for delete actions.
 */
export const Comment: FC<CommentProps> = ({
  comment,
  organizer,
  clipped,
  showRegisteredBadge,
  canDelete,
  onDelete,
}) => {
  const isUpdate = comment.type === "UPDATE";
  const showOrganizerBadge = comment.isOrganizerAdmin;
  const authorName = isUpdate
    ? (organizer?.name ?? "Event Organizer")
    : getProfileName(comment.authorProfile);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteComment({ data: comment.id });
      onDelete?.(comment.id);
      toastManager.add({ title: "Comment deleted", type: "success" });
    } catch (err) {
      toastManager.add({
        title:
          err instanceof Error ? err.message : "Failed to delete comment",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
    }
  }

  return (
    <div
      className={[
        "group",
        clipped ? "relative" : undefined,
        isUpdate
          ? "border border-secondary/25 bg-secondary-50/40 p-2 rounded-md"
          : undefined,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {" "}
      <div
        className={`flex gap-3 ${clipped ? "overflow-hidden max-h-16" : ""}`}
      >
        {isUpdate ? (
          <Avatar organizer={organizer} />
        ) : (
          <Avatar profile={comment.authorProfile} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1">
            <Typography
              level="B3"
              className="font-medium tracking-wide text-gray-900"
            >
              {authorName}
            </Typography>
            {showOrganizerBadge && (
              <Pill size="xs" className="bg-secondary-200/50 border-secondary-500">
                <Typography level="C3" className="text-secondary-500 font-semibold">
                  ORGANIZERS
                </Typography>
              </Pill>
            )}
            {showRegisteredBadge && (
              <Pill size="xs" className="bg-success-200/50 border-success-500">
                <Typography level="C3" className="text-success-500 font-semibold">
                  REGISTERED
                </Typography>
              </Pill>
            )}
            <span className="text-xs text-gray-400">&bull;</span>
            <Typography level="C2" className="text-gray-400">
              {formatRelativeTime(comment.createdAt)}
            </Typography>
            {canDelete && (
              <div className="ml-auto md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <Menu>
                  <MenuTrigger
                    className="p-1 rounded hover:bg-gray-100 cursor-pointer text-gray-400 hover:text-gray-600"
                    aria-label="Comment actions"
                  >
                    <Ellipsis className="size-4" />
                  </MenuTrigger>
                  <MenuPopup align="end">
                    <MenuItem
                      variant="destructive"
                      onClick={() => setConfirmOpen(true)}
                    >
                      <Trash2 className="size-4" />
                      <Typography level="B3">Delete</Typography>
                    </MenuItem>
                  </MenuPopup>
                </Menu>
              </div>
            )}
          </div>
          <Typography level="B3" className="text-gray-700 whitespace-pre-wrap">
            {comment.content}
          </Typography>
        </div>
      </div>
      {/* Fade gradient for the clipped second comment */}
      {clipped && (
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      )}
      {canDelete && (
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete comment?</AlertDialogTitle>
              <AlertDialogDescription>
                This can&apos;t be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogClose
                render={<Button variant="outline">Cancel</Button>}
              />
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      )}
    </div>
  );
};
